import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsuario } from '../../context/usuarioContext';
// Componentes UI
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Toolbar } from 'primereact/toolbar';
import { FilterMatchMode } from 'primereact/api';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { classNames } from 'primereact/utils';
// Componentes
import Permiso from '../../components/wrappers/permiso';
import TableLayout from "../../layouts/layout-tabla";
// Datos
import { obtenerMisRevisionesVehiculares, obtenerCklVehiculo } from '../../services/conductoresService.js';
// Utils
import { exportarExcel } from '../../utils/exportarExcel.js';
import { exportarPDF } from '../../utils/pdf/pdfDataTable.js';
import { generarPDF } from "../../utils/pdf/pdfCklVehiculo.js";
import { formatDateTimeFront, formatFecha, inicioDelDia, finDelDia } from '../../utils/dateFormatter.js';
// Estilos y Assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import useMedia from '../../hooks/media-querys.js';
import '../../pages/panel-prevencion/panel-prevencion.css';

function MiRevisionVehicular() {
  const isPhone  = useMedia('(max-width: 640px)');
  const labelPdf   = isPhone ? 'PDF'   : 'Exportar PDF';
  const labelExcel = isPhone ? 'Excel' : 'Exportar Excel';
  const labelBuscar = isPhone ? '' : 'Buscar';
  const fixWidth = isPhone ? "80px" : "auto";
  const fixSize = isPhone ? "0.75rem" : "1rem";
  
  const { tienePermiso } = useUsuario();
  const puedeVerDet = tienePermiso('Panel Conductor', 'CON_VER_DETVEH');
  
  const toast = useRef(null);
  const dt = useRef(null);
  const navigate = useNavigate();
  
  // Filtro por rango de fechas
  const [dates, setDates] = useState(null);

  // Obtener registros de vehiculo por periodo
  const [informes, setInformes] = useState([]);
  useEffect(() => { obtenerRegistros(); }, []);

  // Barra de Busqueda
  const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
  const [filteredvalue, setFilteredvalue] = useState([]);
  const [globalFilterValue, setGlobalFilterValue] = useState('');
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;

    let _filters = { ...filters };
    _filters['global'].value = value;
    setFilters(_filters);
    setGlobalFilterValue(value);

    // Registros filtrados
    const filtrado = informes.filter((item) => {
      return Object.values(item).some((val) =>
        String(val).toLowerCase().includes(value.toLowerCase())
      );
    });
    setFilteredvalue(filtrado);
  };

  // Utilidad: detectar iOS/iPadOS (incluye iPad con userAgent de Mac)
  const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent)
    || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  
  const verRegistro = (id_checklist) => {
    // 1) Pre-abrir la ventana para no perder el gesto del usuario
    const win = window.open('', '_blank');
    if (!win) {
      toast.current.show({
        severity: 'warn',
        summary: 'Pop-up bloqueado',
        detail: 'Habilita las ventanas emergentes para ver el PDF.',
        life: 4000
      });
      return;
    }

    (async () => {
      try {
        const datos = await obtenerCklVehiculo(id_checklist);
        
        if (!datos) throw new Error('No se recibieron datos');
        
        const pdf = generarPDF(datos);

        if (isIOS()) {
          // 2) iOS: usar Blob + object URL
          pdf.getBlob((blob) => {
            const url = URL.createObjectURL(blob);
            win.location = url; // asignar a la ventana ya abierta
            win.onbeforeunload = () => URL.revokeObjectURL(url);
          });
        } else {
          // 3) Otros navegadores: abrir dentro de la misma ventana pre-abierta
          pdf.open({}, win); // pdfMake acepta la referencia de ventana destino
        }
      } catch (error) {
        // cerrar la ventana pre-abierta si algo falla
        try { win.close(); } catch {}
        toast.current.show({ severity: 'error', summary: 'Error', life: 3000,
          detail: `Error al cargar el informe: ${id_checklist || 'desconocido'}.`
        });
        console.error('Error cargando informe:', error);
      }
    })();
  };

  // Obtener registros desde el servicio
  const obtenerRegistros = (desde, hasta) => {
    obtenerMisRevisionesVehiculares(desde, hasta)
      .then(data => {
        const formatData = data.map(item => ({
          ...item,
          creado_en: formatDateTimeFront(item.creado_en),
          vencDecreto80: formatFecha(item.vencDecreto80) || 'N/A',
          vencRevision: formatFecha(item.vencRevision) || 'N/A',
          vencCirculacion: formatFecha(item.vencCirculacion) || 'N/A',
          vencExtintor: formatFecha(item.vencExtintor) || 'N/A',
        }));
        setInformes(formatData);
      })
      .catch (error => {
        toast.current.show({ severity: 'error', summary: 'Error', life: 3000,
          detail: 'No se pudieron cargar los informes.'
        });
      });
  };

  // Buscar registros por rango de fechas
  const buscarRegistros = () => {
    if (!dates || dates.length !== 2) {
      toast.current.show({ severity: 'info', summary: 'Seleccione un periodo', life: 4000, detail: 'Ingrese un periodo para consultar registros.' });
      obtenerRegistros();
      return;
    }
    const [desdeDate, hastaDate] = dates;
    const desde = inicioDelDia(desdeDate); // YYYY-MM-DD 00:00:00
    const hasta = finDelDia(hastaDate); // YYYY-MM-DD 23:59:59
    obtenerRegistros(desde, hasta);
  };

  // Templates Columnas
    // Accion 
  const accionTemplate = (rowData) => (
    <div className="accion-container">
      <Button 
        className="btn-editar" 
        icon="pi pi-eye"
        tooltip="Ver detalle"
        tooltipOptions={{ position: 'top' }}
        onClick={() => verRegistro(rowData.id_checklist)}
        disabled={!puedeVerDet}
      />
    </div>
  );
    // Cantidad de faltas
  const faltasTemplate = (rowData) => {
    const faltasClassName = classNames('alertas-badge', { 
      'alerta-roja': rowData.cantFaltas >= 1 });
    return <div className={faltasClassName}>{rowData.cantFaltas}</div>;
  };
    // Cantidad necesitan revisión
  const revisarTemplate = (rowData) => {
    const revisarClassName = classNames('alertas-badge', { 
        'alerta-amarilla': rowData.cantRevisar >= 1, 
        'alerta-roja': rowData.cantRevisar >= 3 });
    return <div className={revisarClassName}>{rowData.cantRevisar}</div>;
  };

  // Vehículo
  const vehiculoTemplate = (rowData) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <strong>{rowData.patente}</strong>
        <><br />
          <small className="text-muted">{rowData.tipo_vehiculo}</small>
        </>
      </div>
    );
  };

  // Conductor
  const conductorTemplate = (rowData) => {
    return (
      <div style={{ textAlign: 'center' }}>
        <strong>{rowData.conductor}</strong>
        <><br />
          <small className="text-muted">
            {rowData.rut_conductor}
          </small>
        </>
      </div>
    );
  };

  // Helpers
  const asText = (v) => (v == null ? '' : String(v));
  
  const formatVehiculoExport = (r) => {
    if (r.patente) { return `${r.patente}\n${r.tipo_vehiculo}`; } else { return ''; }
  };
  const formatConductorExport = (r) => {
    if (r.conductor) { return `${r.conductor}\n${r.rut_conductor}`; } else { return ''; }
  };

  // === Columnas para .xlsx ===
  const columnasExcel = [
    { header: 'CHK',                       key: 'cod_checklist',   width: 18, get: r => asText(r['cod_checklist']) },
    { header: 'Fecha Registro',            key: 'creado_en',       width: 22, get: r => asText(r['creado_en']) },
    { header: 'Conductor',                 key: 'conductor',       width: 22, get: r => asText(r['conductor']) },
    { header: 'RUT Conductor',             key: 'rut_conductor',   width: 14, get: r => asText(r['rut_conductor']) },
    { header: 'Patente',                   key: 'patente',         width: 14, get: r => asText(r['patente']) },
    { header: 'Tipo Vehículo',             key: 'tipo_vehiculo',   width: 14, get: r => asText(r['tipo_vehiculo']) },
    { header: 'Venc. Decreto 80',          key: 'vencDecreto80',   width: 16, get: r => asText(r['vencDecreto80']) },
    { header: 'Venc. Revisión Técnica',    key: 'vencRevision',    width: 16, get: r => asText(r['vencRevision']) },
    { header: 'Venc. Permiso Ciruclación', key: 'vencCirculacion', width: 16, get: r => asText(r['vencCirculacion']) },
    { header: 'Mantención Extintor',       key: 'vencExtintor',    width: 16, get: r => asText(r['vencExtintor']) },
    { header: 'Cant. Partes Buenas',       key: 'cantBueno',       width: 14, get: r => asText(r['cantBueno']) },
    { header: 'Cant. Partes Revisar',      key: 'cantRevisar',     width: 14, get: r => asText(r['cantRevisar']) },
    { header: 'Cant. Partes Faltantes',    key: 'cantFaltas',      width: 14, get: r => asText(r['cantFaltas']) }
  ];
  const columnasPDF = [
    { header: 'CHK',                       get: r => asText(r.cod_checklist), width: 55},
    { header: 'Fecha Registro',            get: r => asText(r.creado_en), width: 55},
    { header: 'Conductor',                 get: r => formatConductorExport(r), width: 80},
    { header: 'Vehículo',                  get: r => formatVehiculoExport(r), width: 49},
    { header: 'Venc. Decreto 80',          get: r => asText(r.vencDecreto80)},
    { header: 'Venc. Revisión Técnica',    get: r => asText(r.vencRevision)},
    { header: 'Venc. Permiso Ciruclación', get: r => asText(r.vencCirculacion)},
    { header: 'Mantención Extintor',       get: r => asText(r.vencExtintor)},

    { header: 'Cant. Partes Buenas',       
      get: r => ({ 
        text: String(r.cantBueno),
        color: 'black',
        margin: [0, 6, 4, 4],
        alignment: 'center',
      }),
      width: 35},
    { 
      header: 'Cant. Partes Revisar', 
      get: r => ({ 
        text: String(r.cantRevisar),
        color: 'black',
        fillColor: r.cantRevisar >= 1 ? '#fff7d1' : '#ffffff',
        bold: r.cantRevisar >= 1,
        margin: [0, 6, 4, 4],
        alignment: 'center',
      }),
      width: 35
    },
    { 
      header: 'Cant. Partes Faltantes',    
      get: r => ({ 
        text: String(r.cantFaltas),
        color: 'black',
        fillColor: r.cantFaltas >= 1 ? '#ffd1d1' : '#ffffff',
        bold: r.cantFaltas >= 1,
        margin: [0, 6, 4, 4],
        alignment: 'center',
      }), 
      width: 42
    },
  ];

  // Toolbar 
  const endToolbar = () => {
    const nombreArchivo = 'resumen-checklist-vehículo';
    const tituloArchivo = 'RESUMEN: CHECKLIST VEHÍCULO';
    const subTituloArchivo = 'Panel del Conductor';
    const rows  = filteredvalue.length > 0 ? filteredvalue : informes;

    return(
      <div className='tabla-toolbar-group'>
        <Permiso modulo="Panel Conductor" permiso="CON_EXPORTAR_PDF">
          <Button 
            className="btn-pdf" 
            label={labelPdf}
            style={{ fontSize: fixSize, width: fixWidth }}
            icon="pi pi-file-pdf"
            onClick={() =>
              exportarPDF({ rows, columns: columnasPDF, nombreArchivo, tituloArchivo, subTituloArchivo })
            }
          />
        </Permiso>
        <Permiso modulo="Panel Conductor" permiso="CON_EXPORTAR_EXCEL">
          <Button
            className="btn-excel"
            label={labelExcel}
            style={{ fontSize: fixSize, width: fixWidth}}
            icon="pi pi-file-excel"
            onClick={() =>
              exportarExcel({ rows, columns: columnasExcel, filename: `${nombreArchivo}.xlsx`, })
            }
          />
        </Permiso>
      </div>
    )
  };

  const startToolbar = () => {
    return(
      <div className='seleccionar-periodo'>
        <Permiso modulo="Panel Conductor" permiso="CON_BUSCARPERIODO_VEH">
          <label className="font-bold">Seleccionar Periodo</label>
          <Calendar
            value={dates} 
            onChange={(e) => setDates(e.value)} 
            selectionMode="range" 
            readOnlyInput
            hideOnRangeSelection
            placeholder="Desde - Hasta"
            dateFormat="dd/mm/y" 
            showButtonBar
            className='input-text-m'
          />
          <Button 
            className="btn btn-primary"
            label= {labelBuscar}
            style={{ fontSize: fixSize, width: isPhone ? "50px" : "auto" }}
            icon="pi pi-search"
            onClick={() => buscarRegistros()}
          />
        </Permiso>
      </div>
    )
  };

  const renderHeader = () => {
    const HeadingTag = isPhone ? "h5" : "h4";

    return (
      <div className="flex flex-wrap gap-2 justify-content-between align-items-center">
        <HeadingTag className="m-0">Resumen: Checklist Vehículo</HeadingTag>
        <IconField iconPosition="left">
          <InputIcon className="pi pi-search pr-2" />
          <InputText 
            value={globalFilterValue} 
            onChange={onGlobalFilterChange}
            className={`pl-5 ${isPhone ? "input-text-s" : ""}`}
            placeholder="Filtrar por..."
          />
        </IconField>
      </div>
    )
  };

  return (
    <TableLayout titulo="Histórico Checklist Vehículo" logo={logo} onVolver={() => navigate('/app/panel-conductor')}>
      <Toast ref={toast} position="center"/>
      <div className="table-container card">
        <Toolbar className="tabla-toolbar gap-2" start={startToolbar} end={endToolbar}></Toolbar>
        <DataTable 
          ref={dt} value={informes} dataKey="cod_checklist"
          className="inv-data-table" size='small' 
          showGridlines selectionMode="single"
          header={renderHeader()} 

          // Barra de busqueda
          filters={filters}
          filteredvalue={filteredvalue.length > 0 ? filteredvalue : null}
          globalFilterFields={[ 
            'cod_checklist', 'conductor', 'rut_conductor', 'patente', 'tipo_vehiculo', 'vencDecreto80',
            'vencRevision', 'vencCirculacion', 'vencExtintor', 'cantBueno', 'cantRevisar', 'cantFaltas' 
          ]}
          filterDisplay="menu"

          // Paginador config
          paginator rows={10} rowsPerPageOptions={[10, 25, 50]}
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
          emptyMessage="No hay registros disponibles."
        >
          <Column field="cod_checklist" header="CHK" style={{ minWidth: '100px'}} sortable/>
          <Column field="creado_en" header="Fecha Registro" style={{ minWidth: '100px'}} sortable/>
          <Column header="Conductor" body={conductorTemplate} sortable style={{textAlign: 'center'}}/>
          <Column header="Vehículo" body={vehiculoTemplate} sortable style={{textAlign: 'center'}}/>

          <Column field="vencDecreto80" header="Venc. Decreto 80" sortable style={{textAlign: 'center'}}/>
          <Column field="vencRevision" header="Venc. Revisión Técnica" sortable style={{textAlign: 'center'}}/>
          <Column field="vencCirculacion" header="Venc. Permiso Ciruclación" sortable style={{textAlign: 'center'}}/>
          <Column field="vencExtintor" header="Mantención Extintor" sortable style={{textAlign: 'center'}}/>
          <Column field="cantBueno" header="Buen Estado" sortable style={{textAlign: 'center'}}/>
          <Column field="cantRevisar" header="Necesita Revisión" sortable body={revisarTemplate} style={{textAlign: 'center'}}/>
          <Column field="cantFaltas" header="Faltantes" sortable body={faltasTemplate} style={{textAlign: 'center'}}/>
          <Column header="Acción" style={{textAlign: 'center', justifyContent: 'center'}} body={accionTemplate}/>
        </DataTable>
      </div>
    </TableLayout>
  );
}

export default MiRevisionVehicular;